import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Lock, Search } from 'lucide-react';
import { useState } from 'react';
import { AccountDrawer } from '@/components/account-drawer';
import { ArabicSuggestionsDialog } from '@/components/arabic-suggestions-dialog';
import { Checkbox } from '@/components/form';
import { OpeningBalanceDialog } from '@/components/opening-balance-dialog';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { takesOpeningBalance, useAccounts, type Account } from '@/lib/accounts';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { formatAmount } from '@/lib/format';
import { useHomeCurrency } from '@/lib/currencies';
import { useOrgId } from '@/lib/org';
import { useOrgRole } from '@/lib/portal';
import { useCompanySettings } from '@/lib/settings';
import { taxWord, useArabicEnabled } from '@/lib/org-locale';
import { nameFor } from '@/lib/arabic';
import { useLanguage, useT } from '@/lib/i18n';

export const Route = createFileRoute('/accounting/chart-of-accounts/')({
  component: ChartOfAccounts,
});

const BALANCE_SHEET = new Set(['asset', 'liability', 'equity']);

/** Screen A1 (document 06): list pattern with the account drawer. */
function ChartOfAccounts() {
  const home = useHomeCurrency();
  const orgId = useOrgId();
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  // staff and the client owner set opening balances (ADR-0023)
  const role = useOrgRole();
  const canSetOpening = role !== null && role !== 'client_viewer';
  const [opening, setOpening] = useState<Account | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [q, setQ] = useState('');
  // Arabic (document 15): the Arabic name under the English one, and a review filter
  const arabicOn = useArabicEnabled();
  const t = useT();
  const lang = useLanguage();
  // in Arabic the Arabic names lead and the English follow; in English the reverse
  const arabicUi = lang === 'ar';
  const [missingArabic, setMissingArabic] = useState(false);
  const [suggest, setSuggest] = useState(false);
  const [drawer, setDrawer] = useState<{ open: boolean; account: Account | null }>({
    open: false,
    account: null,
  });
  const [confirm, setConfirm] = useState<Account | null>(null);
  const accounts = useAccounts({ includeInactive });
  const navigate = useNavigate();
  // Company settings › Advanced › Enable account numbers (QBO): hides the Number column
  const showNumbers = useCompanySettings().data?.advanced.enableAccountNumbers ?? true;

  const needle = q.trim().toLowerCase();
  const rows = (accounts.data ?? []).filter(
    (a) =>
      (!arabicOn || !missingArabic || !a.nameAr) &&
      (!needle ||
        a.fullName.toLowerCase().includes(needle) ||
        (a.nameAr ?? '').includes(q.trim()) ||
        a.code.toLowerCase().startsWith(needle) ||
        a.detailTypeName.toLowerCase().includes(needle)),
  );

  return (
    <>
      <PageHeader
        title={t('accounts.title')}
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to="/reports/$reportKey" params={{ reportKey: 'account-list' }}>
                {t('common.runReport')}
              </Link>
            </Button>
            {canEdit ? (
              <>
                {arabicOn ? (
                  <Button variant="outline" onClick={() => setSuggest(true)}>
                    {t('accounts.suggestArabic')}
                  </Button>
                ) : null}
                <Button variant="outline" asChild>
                  <Link to="/settings/import/$kind" params={{ kind: 'accounts' }}>
                    {t('common.import')}
                  </Link>
                </Button>
                <Button onClick={() => setDrawer({ open: true, account: null })}>
                  {t('common.new')}
                </Button>
              </>
            ) : null}
          </>
        }
      />
      {!orgId ? (
        <EmptyState title={t('shell.selectClientTitle')} hint={t('shell.selectClientHint')} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="relative w-full sm:w-[320px]">
              <Search className="pointer-events-none absolute start-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card ps-8"
                placeholder={t('accounts.filterPlaceholder')}
                aria-label={t('accounts.filterLabel')}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Checkbox
              label={t('accounts.includeInactive')}
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            {arabicOn ? (
              <Checkbox
                label={t('accounts.missingArabic')}
                checked={missingArabic}
                onChange={(e) => setMissingArabic(e.target.checked)}
              />
            ) : null}
            {accounts.data ? (
              <span className="ms-auto text-sm text-muted-foreground">
                {t('accounts.count', { count: rows.length })}
              </span>
            ) : null}
          </div>
          {accounts.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : accounts.isError ? (
            <EmptyState title={t('accounts.loadError')} hint={accounts.error.message} />
          ) : rows.length === 0 ? (
            <EmptyState title={t('accounts.noMatch')} />
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    {showNumbers ? (
                      <TableHead className="w-[90px]">{t('accounts.number')}</TableHead>
                    ) : null}
                    <TableHead>{t('accounts.name')}</TableHead>
                    <TableHead>{t('accounts.accountType')}</TableHead>
                    <TableHead>{t('accounts.detailType')}</TableHead>
                    <TableHead className="w-[70px]">{taxWord()}</TableHead>
                    <TableHead className="w-[150px] text-end">
                      {home ? t('accounts.balanceIn', { currency: home }) : t('accounts.balance')}
                    </TableHead>
                    <TableHead className="w-[190px] text-end">{t('accounts.action')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((a) => {
                    const isBs = BALANCE_SHEET.has(a.class);
                    const register = () =>
                      void navigate({
                        to: '/accounting/chart-of-accounts/$accountId/register',
                        params: { accountId: a.id },
                      });
                    // QBO: Run report opens the account's transactions (QuickReport)
                    const report = () =>
                      void navigate({
                        to: '/reports/$reportKey',
                        params: { reportKey: 'transaction-detail-by-account' },
                        search: { accounts: a.id },
                      });
                    return (
                      <TableRow key={a.id} className={a.isActive ? '' : 'text-muted-foreground'}>
                        {showNumbers ? <TableCell className="tnum">{a.code}</TableCell> : null}
                        <TableCell>
                          <span
                            className="inline-flex items-center gap-1.5"
                            style={{ paddingInlineStart: a.depth * 18 }}
                          >
                            <bdi>{nameFor(lang, a.name, a.nameAr)}</bdi>
                            {a.systemKey || a.isControl ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Lock
                                    className="size-3.5 text-muted-foreground"
                                    aria-label={t('accounts.systemAccount')}
                                  />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {a.isControl
                                    ? t('accounts.controlAccountTip')
                                    : t('accounts.systemAccountTip')}
                                </TooltipContent>
                              </Tooltip>
                            ) : null}
                            {!a.isActive ? (
                              <Badge variant="outline">{t('common.inactive')}</Badge>
                            ) : null}
                          </span>
                          {/* the other language's name, under the reader's */}
                          {arabicOn && a.nameAr ? (
                            <div
                              lang={arabicUi ? 'en' : 'ar'}
                              dir={arabicUi ? 'ltr' : 'rtl'}
                              className="w-fit text-xs text-muted-foreground"
                              style={{ marginInlineStart: a.depth * 18 }}
                            >
                              {arabicUi ? a.name : a.nameAr}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          {nameFor(lang, a.accountTypeName, a.accountTypeNameAr)}
                        </TableCell>
                        <TableCell>{nameFor(lang, a.detailTypeName, a.detailTypeNameAr)}</TableCell>
                        <TableCell>{a.defaultTaxCode ?? ''}</TableCell>
                        <TableCell className="tnum text-end">
                          {isBs ? formatAmount(a.balance) : ''}
                          {isBs && a.foreignBalance !== null ? (
                            <div className="text-xs text-muted-foreground">
                              {a.accountCurrency} {formatAmount(a.foreignBalance)}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-end">
                          <div className="inline-flex">
                            <Button
                              size="sm"
                              variant="link"
                              className="px-2"
                              onClick={isBs ? register : report}
                            >
                              {isBs ? t('common.viewRegister') : t('common.runReport')}
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon-sm"
                                  variant="ghost"
                                  aria-label={t('accounts.moreActions', { name: a.name })}
                                >
                                  <ChevronDown className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {canEdit ? (
                                  <DropdownMenuItem
                                    onSelect={() => setDrawer({ open: true, account: a })}
                                  >
                                    {t('common.edit')}
                                  </DropdownMenuItem>
                                ) : null}
                                {canEdit && a.isActive ? (
                                  <DropdownMenuItem
                                    disabled={Boolean(a.systemKey || a.isControl)}
                                    onSelect={() => setConfirm(a)}
                                  >
                                    {t('accounts.makeInactive')}
                                  </DropdownMenuItem>
                                ) : null}
                                {canEdit && !a.isActive ? <ReactivateItem account={a} /> : null}
                                {canSetOpening && takesOpeningBalance(a) ? (
                                  <DropdownMenuItem onSelect={() => setOpening(a)}>
                                    {t('accounts.setOpeningBalance')}
                                  </DropdownMenuItem>
                                ) : null}
                                <DropdownMenuItem onSelect={isBs ? report : register}>
                                  {isBs ? t('common.runReport') : t('common.viewRegister')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
      <OpeningBalanceDialog account={opening} onClose={() => setOpening(null)} />
      <AccountDrawer
        open={drawer.open}
        account={drawer.account}
        onOpenChange={(open) => setDrawer((d) => ({ ...d, open }))}
      />
      <MakeInactiveDialog account={confirm} onClose={() => setConfirm(null)} />
      {arabicOn ? <ArabicSuggestionsDialog open={suggest} onOpenChange={setSuggest} /> : null}
    </>
  );
}

function ReactivateItem({ account }: { account: Account }) {
  const orgId = useOrgId();
  const t = useT();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () =>
      api<Account>(`/orgs/${orgId}/accounts/${account.id}/reactivate`, { method: 'POST' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['accounts', orgId] });
      toast(t('accounts.activeAgain', { name: account.name }));
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });
  return (
    <DropdownMenuItem onSelect={() => m.mutate()}>{t('accounts.makeActive')}</DropdownMenuItem>
  );
}

function MakeInactiveDialog({
  account,
  onClose,
}: {
  account: Account | null;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const t = useT();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (a: Account) =>
      api<Account>(`/orgs/${orgId}/accounts/${a.id}/deactivate`, { method: 'POST' }),
    onSuccess: async (a) => {
      await qc.invalidateQueries({ queryKey: ['accounts', orgId] });
      toast(t('accounts.nowInactive', { name: a.name }));
      onClose();
    },
  });
  return (
    <Dialog
      open={Boolean(account)}
      onOpenChange={(open) => {
        if (!open) {
          m.reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('accounts.inactiveTitle', { name: account?.name ?? '' })}</DialogTitle>
          <DialogDescription>{t('accounts.inactiveBody')}</DialogDescription>
        </DialogHeader>
        {m.error ? (
          <p role="alert" className="text-sm text-destructive">
            {m.error.message}
          </p>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            disabled={m.isPending}
            onClick={() => account && m.mutate(account)}
          >
            {t('accounts.makeInactive')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
